
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { ONBOARDING_OWNER_KEY, clearOnboardingSession, isOnboardingKey } from '@/utils/onboarding/sessionState';

export interface FormData {
  companyData?: any;
  connectionMap?: Record<string, string>;
  savedFormStep?: string;
  selectedConnectorData?: any;
  draftId?: string;
  [key: string]: any;
}

export const saveFormDataWithToken = async (): Promise<string | null> => {
  try {
    // Collect all relevant data from sessionStorage
    const formData: FormData = {};
    
    // Get all keys that start with 'figabl_' or are connection-related
    Object.keys(sessionStorage).forEach(key => {
      if (isOnboardingKey(key) || key === ONBOARDING_OWNER_KEY) {
        const value = sessionStorage.getItem(key);
        if (value) {
          try {
            // Try to parse as JSON, fallback to string
            formData[key] = JSON.parse(value);
          } catch {
            formData[key] = value;
          }
        }
      }
    });
        
    const { data, error } = await invokeFunction(supabase, 'manage-form-token', {
      body: { formData }
    });
    
    if (error) {
      console.error('Error saving form data:', error);
      return null;
    }
    
    return data.token;
  } catch (error) {
    console.error('Error in saveFormDataWithToken:', error);
    return null;
  }
};

export const loadFormDataFromToken = async (token: string): Promise<FormData | null> => {
  try {    
    const { data, error } = await invokeFunction(supabase, 'manage-form-token', {
      body: { token }
    });
    
    if (error) {
      console.error('Error loading form data:', error);
      return null;
    }
    
    const formData = data.formData;
    
    // Replace, don't merge: restoring on top of existing state would blend this token's
    // organisation with whatever the tab already held — the cross-device form of the same
    // contamination bug.
    if (formData && typeof formData === 'object') {
      clearOnboardingSession();

      Object.entries(formData).forEach(([key, value]) => {
        try {
          sessionStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
        } catch (error) {
          console.warn('Failed to restore sessionStorage item:', key, error);
        }
      });
    }
    
    return formData;
  } catch (error) {
    console.error('Error in loadFormDataFromToken:', error);
    return null;
  }
};

export const updateFormToken = async (token: string): Promise<boolean> => {
  try {
    const formData: FormData = {};
    
    Object.keys(sessionStorage).forEach(key => {
      if (isOnboardingKey(key) || key === ONBOARDING_OWNER_KEY) {
        const value = sessionStorage.getItem(key);
        if (value) {
          try {
            formData[key] = JSON.parse(value);
          } catch {
            formData[key] = value;
          }
        }
      }
    });

    const { error } = await invokeFunction(supabase, 'manage-form-token', {
      body: { token, formData, update: true }
    });
    return !error;
  } catch {
    return false;
  }
};

export const getTokenFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('form_token');
};

export const removeTokenFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('form_token');
  window.history.replaceState({}, '', url.toString());
};
